import { action, computed, makeObservable, observable } from 'mobx'

import {
  BicCompletionMode,
  CalendricalType,
  IInspectionOptions,
  IWorkflowStep,
  WorkflowStepType,
} from '~/client/graph'
import { defaultNotificationPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { AndOrOperator } from '~/client/src/shared/models/LogicOperation'
import OperationRule from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import { IWorkflowTypeAction } from '~/client/src/shared/models/WorkflowType'
import PermitBallInCourtStore from '~/client/src/shared/stores/ui/PermitBallInCourt.store'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { copyObjectDeep } from '~/client/src/shared/utils/util'

import BicRulesDraft from '../../BicRulesDraft'

const defaultInspectionOptions: IInspectionOptions = {
  inspectionFrequency: 1,
  inspectionFrequencyType: CalendricalType.Day,
  deadlineTime: 24,
  selectedDaysToRepeat: [],
}

const isExistingRuleId = (ruleId: string): boolean =>
  !!ruleId && ruleId !== OperationRule.NEW_ID

export default class WorkflowStore {
  @observable public removingRuleId: string = null
  // The id the "+ Add" placeholder took on its first edit. Its card stays keyed as the
  // placeholder until another is shown, so that edit doesn't remount it mid-click.
  @observable public newRuleId: string = null
  @observable public isDeleteStepModalShown: boolean = false
  @observable public isStepDescrDialogShown: boolean = false
  @observable public isAutoToggleDialogShown: boolean = false

  @observable public permitTypeId: string = null
  @observable public isAutoActivationEnabled = false
  @observable public workflowSteps: IWorkflowStep[] = []
  @observable public inspectionOptions: IInspectionOptions = null

  private removingStepIndex: number

  public constructor(
    private readonly bicRulesDraft: BicRulesDraft,
    private readonly permitBallInCourtStore: PermitBallInCourtStore,
    formTypeId: string,
    workflowSteps: IWorkflowStep[],
    inspectionOptions: IInspectionOptions,
    isAutoActivationEnabled: boolean,
    private readonly saveWorkflowSettings: (
      workflowSteps: IWorkflowStep[],
      inspectionOptions: IInspectionOptions,
      isAutoActivationEnabled: boolean,
    ) => void,
  ) {
    this.init(
      formTypeId,
      workflowSteps,
      inspectionOptions,
      isAutoActivationEnabled,
    )
    makeObservable(this)
  }

  @action.bound
  public init(
    formTypeId: string,
    workflowSteps: IWorkflowStep[],
    inspectionOptions: IInspectionOptions,
    isAutoActivationEnabled: boolean,
  ) {
    this.permitTypeId = formTypeId
    this.workflowSteps = workflowSteps.map(s => copyObjectDeep(s))
    this.inspectionOptions = copyObjectDeep(inspectionOptions)
    this.isAutoActivationEnabled = isAutoActivationEnabled || false

    this.hideNewRuleRow()
    this.hideDeleteStepModal()
  }

  @action.bound
  public showDeleteStepModal(stepIndex: number) {
    this.removingStepIndex = stepIndex
    this.isDeleteStepModalShown = true
  }

  @action.bound
  public hideDeleteStepModal() {
    this.isDeleteStepModalShown = false
    this.removingStepIndex = null
  }

  @action.bound
  public showStepDescrDialog() {
    this.isStepDescrDialogShown = true
  }

  @action.bound
  public hideStepDescrDialog() {
    this.isStepDescrDialogShown = false
  }

  @action.bound
  public showAutoToggleDialog() {
    this.isAutoToggleDialogShown = true
  }

  @action.bound
  public hideAutoToggleDialog() {
    this.isAutoToggleDialogShown = false
  }

  @action.bound
  public hideAllDialogs() {
    this.hideAutoToggleDialog()
    this.hideDeleteStepModal()
  }

  @action.bound
  public addNewWorkflowStep(index: number, stepType: WorkflowStepType) {
    const newStep: IWorkflowStep = {
      id: BsonObjectId.generate(),
      type: stepType,
      fields: [],
      conditionalFields: [],
      workflowRuleIds: [],
      notificationFieldIds: this.getDefaultNotificationFieldIds(stepType),
      notificationUserIds: [],
    }
    this.workflowSteps = [
      ...this.workflowSteps.slice(0, index + 1),
      newStep,
      ...this.workflowSteps.slice(index + 1),
    ]

    if (stepType === WorkflowStepType.Start) {
      this.correctRecurringStepPosition()
    }

    this.saveWorkflowChanges()
  }

  @action.bound
  public replaceWorkflowStep(index: number, newStepType: WorkflowStepType) {
    const existingStep = this.workflowSteps[index]

    const updatedStep: IWorkflowStep = {
      ...existingStep,
      type: newStepType,
    }

    this.workflowSteps = [
      ...this.workflowSteps.slice(0, index),
      updatedStep,
      ...this.workflowSteps.slice(index + 1),
    ]

    this.saveWorkflowChanges()
  }

  @action.bound
  public confirmRemoveWorkflowStep() {
    if (
      !this.isAutoActivationEnabled &&
      this.workflowSteps[this.removingStepIndex]?.type ===
        WorkflowStepType.Start &&
      this.hasRecurringStep
    ) {
      this.isDeleteStepModalShown = false
      return this.showAutoToggleDialog()
    }

    this.removeWorkflowStep()
  }

  @action.bound
  public confirmAutoActivationChange() {
    if (this.isAutoActivationEnabled) {
      const approvalStepIdx = this.workflowSteps.findIndex(
        s => s.type === WorkflowStepType.Approval,
      )

      this.isAutoActivationEnabled = !this.isAutoActivationEnabled
      this.hideAutoToggleDialog()

      this.addNewWorkflowStep(approvalStepIdx, WorkflowStepType.Start)
    } else {
      this.isAutoActivationEnabled = !this.isAutoActivationEnabled
      this.hideAutoToggleDialog()

      this.removeWorkflowStep()
    }
  }

  @action.bound
  private correctRecurringStepPosition() {
    const recurringStep = this.workflowSteps.find(
      s => s.type === WorkflowStepType.RecurringInspection,
    )

    if (!recurringStep) return

    this.workflowSteps = this.workflowSteps.filter(
      s => s.type !== WorkflowStepType.RecurringInspection,
    )
    const idx = this.hasStartStep
      ? this.workflowSteps.findIndex(s => s.type === WorkflowStepType.Start)
      : this.workflowSteps.findIndex(s => s.type === WorkflowStepType.Approval)

    if (idx !== -1) {
      this.workflowSteps.splice(idx + 1, 0, recurringStep)
    }
  }

  @action.bound
  public changeInspectionOptions(inspectionOptions: IInspectionOptions) {
    this.inspectionOptions = inspectionOptions

    this.saveWorkflowChanges()
  }

  @action.bound
  public toggleAutoActivationState() {
    if (
      this.isAutoActivationEnabled &&
      this.hasRecurringStep &&
      !this.hasStartStep
    ) {
      return this.showAutoToggleDialog()
    }

    this.isAutoActivationEnabled = !this.isAutoActivationEnabled

    this.saveWorkflowChanges()
  }

  @action.bound
  public changeNotificationFieldIds(
    stepId: string,
    fieldId: string,
    isSelected: boolean,
  ) {
    const stepIndex = this.workflowSteps.findIndex(s => s.id === stepId)
    if (stepIndex === -1) return

    const step = this.workflowSteps[stepIndex]
    const hasField = step.notificationFieldIds.includes(fieldId)

    if (isSelected === hasField) return

    const updatedStep: IWorkflowStep = {
      ...step,
      notificationFieldIds: isSelected
        ? [...step.notificationFieldIds, fieldId]
        : step.notificationFieldIds.filter(id => id !== fieldId),
    }

    this.workflowSteps = [
      ...this.workflowSteps.slice(0, stepIndex),
      updatedStep,
      ...this.workflowSteps.slice(stepIndex + 1),
    ]

    this.saveWorkflowChanges()
  }

  @action.bound
  public changeNotificationFieldIdsBulk(
    stepId: string,
    fieldIds: string[],
    isSelected: boolean,
  ) {
    const stepIndex = this.workflowSteps.findIndex(s => s.id === stepId)
    if (stepIndex === -1) return

    const step = this.workflowSteps[stepIndex]
    const currentIds = step.notificationFieldIds || []

    const updatedStep: IWorkflowStep = {
      ...step,
      notificationFieldIds: isSelected
        ? [...new Set([...currentIds, ...fieldIds])]
        : currentIds.filter(id => !fieldIds.includes(id)),
    }

    this.workflowSteps = [
      ...this.workflowSteps.slice(0, stepIndex),
      updatedStep,
      ...this.workflowSteps.slice(stepIndex + 1),
    ]

    this.saveWorkflowChanges()
  }

  @action.bound
  public changeNotificationUserIds(
    stepId: string,
    userId: string,
    isSelected: boolean,
  ) {
    const stepIndex = this.workflowSteps.findIndex(s => s.id === stepId)
    if (stepIndex === -1) return

    const step = this.workflowSteps[stepIndex]
    const notificationUserIds = step.notificationUserIds || []
    const hasUser = notificationUserIds.includes(userId)
    if (isSelected === hasUser) return

    const updatedStep: IWorkflowStep = {
      ...step,
      notificationUserIds: isSelected
        ? [...notificationUserIds, userId]
        : notificationUserIds.filter(id => id !== userId),
    }

    this.workflowSteps = [
      ...this.workflowSteps.slice(0, stepIndex),
      updatedStep,
      ...this.workflowSteps.slice(stepIndex + 1),
    ]

    this.saveWorkflowChanges()
  }

  @action.bound
  public changeNotificationUserIdsBulk(
    stepId: string,
    userIds: string[],
    isSelected: boolean,
  ) {
    const stepIndex = this.workflowSteps.findIndex(s => s.id === stepId)
    if (stepIndex === -1) return

    const step = this.workflowSteps[stepIndex]
    const currentIds = step.notificationUserIds || []

    const updatedStep: IWorkflowStep = {
      ...step,
      notificationUserIds: isSelected
        ? [...new Set([...currentIds, ...userIds])]
        : currentIds.filter(id => !userIds.includes(id)),
    }

    this.workflowSteps = [
      ...this.workflowSteps.slice(0, stepIndex),
      updatedStep,
      ...this.workflowSteps.slice(stepIndex + 1),
    ]

    this.saveWorkflowChanges()
  }

  @action.bound
  public changeNotificationDiff(
    stepId: string,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
  ) {
    if (
      !addedUserIds.length &&
      !removedUserIds.length &&
      !addedFieldIds.length &&
      !removedFieldIds.length
    ) {
      return
    }

    const stepIndex = this.workflowSteps.findIndex(s => s.id === stepId)
    if (stepIndex === -1) return

    const step = this.workflowSteps[stepIndex]
    const currentUserIds = step.notificationUserIds || []
    const currentFieldIds = step.notificationFieldIds || []

    const afterUserRemoves = currentUserIds.filter(
      id => !removedUserIds.includes(id),
    )
    const afterFieldRemoves = currentFieldIds.filter(
      id => !removedFieldIds.includes(id),
    )

    const updatedStep: IWorkflowStep = {
      ...step,
      notificationUserIds: [...new Set([...afterUserRemoves, ...addedUserIds])],
      notificationFieldIds: [
        ...new Set([...afterFieldRemoves, ...addedFieldIds]),
      ],
    }

    this.workflowSteps = [
      ...this.workflowSteps.slice(0, stepIndex),
      updatedStep,
      ...this.workflowSteps.slice(stepIndex + 1),
    ]

    this.saveWorkflowChanges()
  }

  @action.bound
  public changeActions(stepId: string, actions: IWorkflowTypeAction[]) {
    this.workflowSteps = this.workflowSteps.map(step => {
      if (step.id === stepId) {
        step.actions = actions
      }
      return step
    })

    this.saveWorkflowChanges()
  }

  /**
   * Both multi-signer modes keep every signer's copy of the step's fields, so the flag stays
   * set in one-sign-off-per-rule mode too: apps that don't know the mode then require everyone.
   */
  @action.bound
  public setBicCompletionMode(stepId: string, mode: BicCompletionMode) {
    this.workflowSteps = this.workflowSteps.map(step => {
      if (step.id === stepId) {
        step.bicCompletionMode = mode
        step.requireAllSubmissions = mode !== BicCompletionMode.AnyOne
      }
      return step
    })

    this.saveWorkflowChanges()
  }

  //#region Operation rules methods
  public getStepRules = (ruleIds: string[]): OperationRule[] =>
    this.bicRulesDraft.getPermitBicRules(ruleIds)

  @action.bound
  public showNewRuleRow(stepId: string) {
    this.hideNewRuleRow()
    this.newRuleId = null

    const workflowStep = this.workflowSteps.find(s => s.id === stepId)
    if (!workflowStep.workflowRuleIds) {
      workflowStep.workflowRuleIds = []
    }
    workflowStep.workflowRuleIds.push(OperationRule.NEW_ID)
  }

  @action.bound
  public hideNewRuleRow() {
    this.workflowSteps = this.workflowSteps.map(s => {
      if (s.workflowRuleIds?.includes(OperationRule.NEW_ID)) {
        s.workflowRuleIds = s.workflowRuleIds.filter(isExistingRuleId)
      }
      return s
    })
  }

  @action.bound
  public tryToRemoveRule(ruleId: string) {
    this.removingRuleId = ruleId
  }

  @action.bound
  public resetRemovingRuleId() {
    this.removingRuleId = null
  }

  @action.bound
  public addOrDeleteUserFromRule(
    rule: OperationRule,
    userId: string,
    isNew?: boolean,
  ) {
    this.editRule(rule, draft =>
      isNew
        ? this.permitBallInCourtStore.addUserToRule(draft, userId)
        : this.permitBallInCourtStore.removeUserFromRule(draft, userId),
    )
  }

  /**
   * One assignee picker save, applied as a single edit: each edit copies the rule it is given,
   * so splitting the save would let its second part overwrite the first.
   */
  @action.bound
  public applyDiffToRule(
    rule: OperationRule,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) {
    this.editRule(rule, draft =>
      this.permitBallInCourtStore.applyDiffToRule(
        draft,
        addedUserIds,
        removedUserIds,
        addedFieldIds,
        removedFieldIds,
        addedCompanyIds,
        removedCompanyIds,
      ),
    )
  }

  @action.bound
  public addOrDeleteFieldFromRule(
    rule: OperationRule,
    fieldId: string,
    isNew?: boolean,
  ) {
    this.editRule(rule, draft =>
      isNew
        ? this.permitBallInCourtStore.addFieldToRule(draft, fieldId)
        : this.permitBallInCourtStore.removeFieldFromRule(draft, fieldId),
    )
  }

  @action.bound
  public addOrDeleteFieldsFromRule(
    rule: OperationRule,
    fieldIds: string[],
    isNew: boolean,
  ) {
    this.editRule(rule, draft =>
      isNew
        ? this.permitBallInCourtStore.addFieldsToRule(draft, fieldIds)
        : this.permitBallInCourtStore.removeFieldsFromRule(draft, fieldIds),
    )
  }

  @action.bound
  public applyCompanyDiffToRule(
    rule: OperationRule,
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) {
    this.editRule(rule, draft =>
      this.permitBallInCourtStore.applyCompanyDiffToRule(
        draft,
        addedCompanyIds,
        removedCompanyIds,
      ),
    )
  }

  @action.bound
  public renameRule(rule: OperationRule, name: string) {
    this.editRule(rule, draft => {
      const trimmedName = name.trim() || null
      if (draft.name === trimmedName) {
        return false
      }

      draft.name = trimmedName
      return true
    })
  }

  @action.bound
  public duplicateRule(ruleId: string) {
    const sourceRule = this.bicRulesDraft.getRule(ruleId)
    const ruleStep = this.workflowSteps.find(s =>
      s.workflowRuleIds?.includes(ruleId),
    )

    if (!sourceRule || !ruleStep) return

    const newRule = sourceRule.getCopy() as OperationRule
    newRule.id = BsonObjectId.generate()
    newRule.setCreatedAt(Date.now())
    newRule.setExpression(
      this.permitBallInCourtStore.withPermitTypeExpression(
        newRule.expression,
        this.permitTypeId,
      ),
    )

    ruleStep.workflowRuleIds = [
      ...ruleStep.workflowRuleIds.filter(isExistingRuleId),
      newRule.id,
    ]
    this.bicRulesDraft.setRule(newRule)

    this.saveWorkflowChanges()
  }

  @action.bound
  public performRemoveRule() {
    if (!this.removingRuleId) {
      return
    }

    if (isExistingRuleId(this.removingRuleId)) {
      this.workflowSteps.forEach(s => {
        s.workflowRuleIds = (s.workflowRuleIds || []).filter(
          id => id !== this.removingRuleId && this.bicRulesDraft.hasRule(id),
        )
        if (s.workflowRuleIds.length === 0 && s.requireAllSubmissions) {
          s.requireAllSubmissions = false
          s.bicCompletionMode = BicCompletionMode.AnyOne
        }
      })
      this.saveWorkflowChanges()
    } else {
      this.hideNewRuleRow()
    }

    this.resetRemovingRuleId()
  }

  @action.bound
  public changeRuleExpression(
    rule: OperationRule,
    locationAndCompanyExpression: string,
  ) {
    const permitTypeExpression =
      this.permitBallInCourtStore.makePermitTypeExpression(this.permitTypeId)

    let newExpression = permitTypeExpression

    if (locationAndCompanyExpression) {
      newExpression = newExpression.concat(
        ` ${AndOrOperator.AND} `,
        locationAndCompanyExpression,
      )
    }

    this.editRule(rule, draft => {
      if (draft.expression === newExpression) {
        return false
      }

      draft.setExpression(newExpression)
      return true
    })
  }

  public getUsersFromExpressions = (
    assignmentExpressions: string[],
  ): User[] => {
    return this.permitBallInCourtStore.getUsersFromAssignment(
      assignmentExpressions,
    )
  }

  public getNotificationUsers = (notificationUserIds: string[]): User[] => {
    return this.permitBallInCourtStore.getUsersByIds(notificationUserIds || [])
  }

  /**
   * Edits a copy of the rule and keeps it in the draft, to be written when the type is saved.
   * The "+ Add" placeholder becomes a rule of its own on its first edit, under an id made here
   * so its step can point at it before either is saved.
   */
  @action.bound
  private editRule(
    rule: OperationRule,
    edit: (draft: OperationRule) => boolean,
  ) {
    const draft = rule.getCopy() as OperationRule

    if (!edit(draft)) return

    if (!draft.isExisting) {
      const newRuleStep = this.workflowSteps.find(s =>
        s.workflowRuleIds?.includes(OperationRule.NEW_ID),
      )

      if (!newRuleStep) return

      draft.id = BsonObjectId.generate()
      draft.setCreatedAt(Date.now())
      this.newRuleId = draft.id
      newRuleStep.workflowRuleIds = [
        ...newRuleStep.workflowRuleIds.filter(isExistingRuleId),
        draft.id,
      ]
    }

    this.bicRulesDraft.setRule(draft)
    this.saveWorkflowChanges()
  }
  //#endregion

  @action.bound
  private removeWorkflowStep() {
    this.workflowSteps = this.workflowSteps.filter(
      (_, index) => index !== this.removingStepIndex,
    )

    this.correctRecurringStepPosition()
    this.correctAutoActivationState()
    this.hideDeleteStepModal()

    this.saveWorkflowChanges()
  }

  @action.bound
  private correctAutoActivationState() {
    if (!this.hasApprovalStep && !this.hasStartStep) {
      this.isAutoActivationEnabled = false
    }
  }

  @computed
  public get isAnyModalShown(): boolean {
    return !!this.removingRuleId || this.isDeleteStepModalShown
  }

  @computed
  public get hasApprovalStep(): boolean {
    return this.workflowSteps.some(s => s.type === WorkflowStepType.Approval)
  }

  @computed
  public get hasStartStep(): boolean {
    return this.workflowSteps.some(s => s.type === WorkflowStepType.Start)
  }

  @computed
  public get hasRecurringStep(): boolean {
    return this.workflowSteps.some(
      s => s.type === WorkflowStepType.RecurringInspection,
    )
  }

  private saveWorkflowChanges = () => {
    this.saveWorkflowSettings(
      this.workflowSteps.map(s => ({
        ...s,
        workflowRuleIds: (s.workflowRuleIds || []).filter(isExistingRuleId),
      })),
      this.inspectionOptionsForSave,
      this.isAutoActivationEnabled,
    )
  }

  private get inspectionOptionsForSave(): IInspectionOptions {
    if (!this.hasRecurringStep) return null

    return this.inspectionOptions?.inspectionFrequency
      ? this.inspectionOptions
      : defaultInspectionOptions
  }

  private getDefaultNotificationFieldIds(stepType: WorkflowStepType): string[] {
    if (stepType === WorkflowStepType.RecurringInspection) {
      return []
    }

    return this.workflowSteps.flatMap(s =>
      s.fields.reduce((list, field) => {
        if (defaultNotificationPermitFieldTypes.includes(field.type)) {
          list.push(field.id)
        }
        return list
      }, [] as string[]),
    )
  }
}
